import { RadarChart } from "@components/charts/RadarChart";
import { HelpLink } from "@components/help/HelpLink";

interface CompetencyScoreData {
  id: string;
  score: number | null;
  capabilityDimension: { id: string; continuityId: string; name: string };
}

export interface CandidateAssessmentSummaryData {
  overallScore: number | null;
  recommendation: string | null;
  overallSummary: string | null;
  competencyScores: CompetencyScoreData[];
}

export interface AssessmentDimensionData {
  id: string;
  continuityId: string;
  name: string;
  idealScore: number | null;
}

interface CandidateAssessmentSummaryProps {
  assessment: CandidateAssessmentSummaryData;
  candidateName?: string;
  dimensions?: AssessmentDimensionData[];
  onViewDetails?: () => void;
}

// Score bands derived from overall_score in CandidateAssessmentVersion.
// The platform never issues a "Strong Yes / Hireable" verdict — these are
// neutral threshold-anchored labels. The recruiter decides via stage
// transitions, not the system.
export const RECOMMENDATION_LABELS: Record<string, string> = {
  missing: "Missing requirements",
  meets: "Meets requirements",
  exceeds: "Exceeds requirements",
};

export const RECOMMENDATION_COLORS: Record<string, string> = {
  missing: "bg-error-bg/60 text-error",
  meets: "bg-warning-bg text-warning",
  exceeds: "bg-success-bg text-success",
};

export function CandidateAssessmentSummary({
  assessment,
  candidateName,
  dimensions,
  onViewDetails,
}: CandidateAssessmentSummaryProps) {
  const showRadar =
    dimensions != null && dimensions.length > 0 && assessment.competencyScores.length > 0;
  const scoresMap: Record<string, number> = {};
  if (showRadar) {
    for (const cs of assessment.competencyScores) {
      if (cs.score != null) scoresMap[cs.capabilityDimension.continuityId] = cs.score;
    }
  }

  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        Assessment
      </h4>
      <div className="rounded border border-border p-2">
        <div className="flex items-center gap-3">
          {assessment.overallScore != null && (
            <span className="text-sm font-semibold text-text-primary">
              {assessment.overallScore}/5
            </span>
          )}
          {assessment.recommendation && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${RECOMMENDATION_COLORS[assessment.recommendation] ?? ""}`}
            >
              {RECOMMENDATION_LABELS[assessment.recommendation] ?? assessment.recommendation}
            </span>
          )}
          {assessment.recommendation && (
            <HelpLink to="/help/score-bands#bands" label="Guide: score bands" />
          )}
        </div>
        {assessment.overallSummary && (
          <p className="mt-1 line-clamp-3 text-xs text-text-secondary">
            {assessment.overallSummary}
          </p>
        )}
        {showRadar && (
          <div className="mt-2 flex justify-center">
            <RadarChart
              dimensions={dimensions}
              candidateScores={scoresMap}
              candidateName={candidateName ?? "Candidate"}
              size="sm"
            />
          </div>
        )}
        {assessment.competencyScores.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {assessment.competencyScores.map((cs) => (
              <span
                key={cs.id}
                className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-text-secondary dark:bg-gray-800"
              >
                {cs.capabilityDimension.name}: {cs.score != null ? `${cs.score}/5` : "—"}
              </span>
            ))}
          </div>
        )}
        {onViewDetails && (
          <button
            type="button"
            onClick={onViewDetails}
            className="mt-2 text-[11px] font-medium text-accent hover:underline"
          >
            View full assessment
          </button>
        )}
      </div>
    </div>
  );
}
